import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Input, InputField, InputSlot } from "@repo/components/src/ui/input";
import { Pressable } from "@repo/components/src/ui/pressable";
import { AlertCircleIcon } from "lucide-react-native";
import { ComponentProps } from "react";
import { Controller, FieldValues } from "react-hook-form";
import { Text } from "../../ui/text";
import {
  caretAfterDigits,
  countDigits,
} from "../affixNumberInput/caretHelpers";
import MeasuredHiddenText from "../affixNumberInput/MeasuredHiddenText";
import { useAutoWidth } from "../affixNumberInput/useAutoWidth";
import {
  bindInputRef,
  clampSelectionToLength,
  focusToEnd,
  useCaretSelection,
} from "../affixNumberInput/useCaretSelection";
import { FormInputProps } from "../types";
import {
  parsePercentageText,
  percentageValueToText,
} from "./parsePercentageInput";

export type FormInputPercentageProps<TFieldValues extends FieldValues> =
  FormInputProps<TFieldValues, number | undefined | null> & {
    inputProps?: ComponentProps<typeof Input>;
    inputFieldProps?: ComponentProps<typeof InputField>;
    /**
     * When `true`, a `null`/`undefined` value renders as an empty field
     * (showing `placeholder`) instead of a formatted "0", and clearing
     * the field sets it back to `undefined` instead of `0`. Defaults to
     * `false` to keep the existing behaviour for every other consumer.
     */
    allowEmpty?: boolean;
  };

export default function FormInputPercentage<TFieldValues extends FieldValues>(
  props: FormInputPercentageProps<TFieldValues>,
) {
  const {
    control,
    name,
    placeholder,
    label,
    required,
    disabled: disabledProp = false,
    className,
    size,
    labelSize = "md",
    inputProps,
    inputFieldProps,
    allowEmpty = false,
  } = props;
  const { width, onMeasure } = useAutoWidth();
  const { selection, setSelection, onSelectionChange, inputRef } =
    useCaretSelection();
  return (
    <Controller
      name={name}
      control={control}
      disabled={disabledProp}
      render={({
        field: { onChange, value, ref, onBlur, disabled },
        fieldState: { error },
      }) => {
        const displayedValue = percentageValueToText(value, allowEmpty);
        const measuredText = displayedValue || (placeholder ?? "");

        return (
          <FormControl
            isInvalid={!!error}
            isRequired={required}
            isDisabled={disabled}
            className={className}
            size={size}
          >
            {!!label && (
              <FormControlLabel>
                <FormControlLabelText size={labelSize}>
                  {label}
                </FormControlLabelText>
              </FormControlLabel>
            )}
            <Pressable
              onPress={() =>
                focusToEnd(inputRef, setSelection, displayedValue.length)
              }
              className="cursor-text"
            >
              <Input
                isDisabled={disabled}
                isInvalid={!!error}
                size={size}
                {...inputProps}
              >
                <MeasuredHiddenText
                  text={measuredText}
                  size={size}
                  onMeasure={onMeasure}
                />
                <InputField
                  ref={bindInputRef(inputRef, ref)}
                  onBlur={onBlur}
                  placeholder={placeholder}
                  value={displayedValue}
                  selection={clampSelectionToLength(
                    selection,
                    displayedValue.length,
                  )}
                  onSelectionChange={onSelectionChange}
                  onChangeText={(typedText: string) => {
                    const previousText = displayedValue;
                    const caret = Math.min(
                      Math.max(
                        selection.start +
                          (typedText.length - previousText.length),
                        0,
                      ),
                      typedText.length,
                    );
                    const digitsBeforeCaret = countDigits(
                      typedText.slice(0, caret),
                    );
                    const nextValue = parsePercentageText(
                      typedText,
                      allowEmpty,
                    );
                    onChange(nextValue);
                    const newDisplayedText = percentageValueToText(
                      nextValue,
                      allowEmpty,
                    );
                    const caretPosition = caretAfterDigits(
                      newDisplayedText,
                      digitsBeforeCaret,
                    );
                    setSelection({ start: caretPosition, end: caretPosition });
                  }}
                  keyboardType="number-pad"
                  textContentType="none"
                  autoCapitalize="none"
                  className="flex-none"
                  style={{ width }}
                  {...inputFieldProps}
                />
                <InputSlot className="pr-3">
                  <Text className="text-typography-500" size={size}>
                    %
                  </Text>
                </InputSlot>
              </Input>
            </Pressable>
            <FormControlError>
              <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
              <FormControlErrorText>{error?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
